import { useState } from "react";
import { Plus, Search, Trash2, MessageSquare, LogOut, Sparkles, ShieldCheck, School, Star, Receipt, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

export const Sidebar = ({ chats, activeId, onSelect, onNew, onDelete, open, user, onLogout, onPlan, onKids, onWords, onBilling, busy }) => {
  const [q, setQ] = useState("");
  const filtered = chats.filter((c) => c.title.toLowerCase().includes(q.toLowerCase()));
  const trialDays = user.trial_ends_at ? Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000) : 0;
  const isStudent = user.role === "student";
  return (
    <aside
      data-testid="chat-history-sidebar"
      className={`what-sidebar ${open ? "is-open" : ""} fixed md:static inset-y-0 left-0 z-40 w-[280px] shrink-0 flex flex-col border-r border-white/[0.07] bg-[#0E131D]`}
    >
      <div className="px-6 pt-7 pb-5 flex items-baseline gap-2">
        <span className="font-display text-3xl font-extrabold tracking-tight text-white">WHAT?</span>
        <span className="what-dot" />
        <span data-testid="sidebar-kid-friendly" title="Child friendly: Kids Mode checks every answer for children under 9" className="ml-auto self-center text-[#38BDF8]"><ShieldCheck size={16} /></span>
      </div>
      <div className="px-4 space-y-3">
        <button data-testid="new-chat-btn" onClick={onNew} className="what-btn-primary w-full">
          <Plus size={16} /> New question
        </button>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="search-history-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search history"
            className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 outline-none focus:border-[#D4FF3A]/40 transition-colors"
          />
        </div>
      </div>
      <p className="px-6 mt-7 mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono">History</p>
      <nav className="flex-1 overflow-y-auto px-3 pb-6 space-y-0.5">
        {filtered.length === 0 && <p className="px-3 py-2 text-sm text-slate-500" data-testid="history-empty">No chats yet</p>}
        {filtered.map((c) => (
          <div
            key={c.id}
            data-testid="chat-history-item"
            onClick={() => onSelect(c.id)}
            className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer text-sm transition-colors ${
              c.id === activeId ? "bg-white/[0.06] text-white" : "text-slate-400 hover:bg-white/[0.03] hover:text-slate-200"
            }`}
          >
            <MessageSquare size={14} className="shrink-0 opacity-60" />
            <span className="truncate flex-1">{c.title}</span>
            <button
              data-testid="delete-chat-btn"
              onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
              className="md:opacity-0 md:group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"
              aria-label="Delete chat"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </nav>
      <div className="px-4 pb-3 space-y-2">
        {user.kids_mode && (
          <button data-testid="my-words-btn" onClick={onWords} className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-mono bg-[#FBBF24]/10 text-[#FBBF24] hover:bg-[#FBBF24]/15 transition-colors">
            <span className="flex items-center gap-1.5"><Star size={13} /> My Words</span>
            <span>→</span>
          </button>
        )}
        {isStudent ? (
          <div data-testid="student-class-badge" className="px-3 py-2 rounded-lg text-xs font-mono bg-white/[0.03] text-slate-400 flex items-center gap-1.5">
            <School size={13} className="text-[#38BDF8]" /> {user.class_name || "My class"}
            {user.plan === "pro" && <span className="ml-auto text-[#D4FF3A]">ALL 3 AIs</span>}
          </div>
        ) : (
          <>
            {!user.kids_mode && (
              <Link to="/school" data-testid="school-link" className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-mono text-slate-400 hover:bg-white/[0.03] transition-colors">
                <span className="flex items-center gap-1.5"><School size={13} /> School & homeschool</span>
                <span>→</span>
              </Link>
            )}
            <button
              data-testid="kids-mode-btn"
              onClick={onKids}
              disabled={busy}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-mono transition-colors ${user.kids_mode ? "bg-[#38BDF8]/10 text-[#38BDF8]" : "text-slate-400 hover:bg-white/[0.03]"}`}
            >
              <span className="flex items-center gap-1.5"><ShieldCheck size={13} /> Kids Mode</span>
              <span data-testid="kids-mode-status">{user.kids_mode ? "ON" : "OFF"}</span>
            </button>
            {user.kids_mode && (
              <button data-testid="sidebar-kid-age-btn" onClick={onKids} className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-mono text-slate-400 hover:bg-white/[0.03] transition-colors">
                <span>Child's age: <span data-testid="sidebar-kid-age" className="text-white">{user.kid_age || "not set"}</span></span>
                <span className="text-[#38BDF8]">Change</span>
              </button>
            )}
          </>
        )}
        {user.kids_mode || isStudent ? null : user.plan === "pro" ? (
          <button data-testid="manage-plan-btn" onClick={onPlan} className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-mono text-slate-400 hover:bg-white/[0.03] transition-colors">
            <span data-testid="plan-badge" className="flex items-center gap-1.5 text-[#D4FF3A]">
              <Sparkles size={12} /> {trialDays > 0 ? `TRIAL · ${trialDays}d` : "PRO"}
            </span>
            manage
          </button>
        ) : (
          <button data-testid="upgrade-btn" onClick={onPlan} className="what-upgrade w-full">
            <span data-testid="plan-badge" className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Free plan</span>
            <span className="flex items-center gap-1.5 text-sm text-white">
              <Sparkles size={14} className="text-[#D4FF3A]" /> {user.trial_used ? "Upgrade to Pro" : "Try Pro free for 14 days"}
            </span>
          </button>
        )}
        {!user.kids_mode && !isStudent && (
          <button data-testid="billing-history-btn" onClick={onBilling} className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-mono text-slate-400 hover:bg-white/[0.03] transition-colors">
            <span className="flex items-center gap-1.5"><Receipt size={13} /> Billing & payments</span>
            <span>→</span>
          </button>
        )}
        <div className="flex items-center gap-3 px-3 pt-1 text-[11px] font-mono text-slate-600">
          <a href="/privacy" data-testid="sidebar-privacy-link" className="hover:text-slate-300">Privacy</a>
          {!isStudent && !user.kids_mode && <Link to="/account" data-testid="sidebar-account-link" className="hover:text-slate-300">Account</Link>}
          {user.role === "admin" && <Link to="/admin/testers" data-testid="sidebar-testers-link" className="hover:text-[#D4FF3A]">Testers</Link>}
          {user.role === "admin" && <Link to="/play-listing" data-testid="sidebar-play-listing-link" className="hover:text-[#D4FF3A]">Play listing</Link>}
          {!isStudent && <a href="/delete-account" data-testid="sidebar-delete-account-link" className="hover:text-red-300">Delete account</a>}
        </div>
      </div>
      <div data-testid="sidebar-user" className="border-t border-white/[0.07] p-4 flex items-center gap-3">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <span className="w-8 h-8 rounded-full bg-[#D4FF3A] text-[#0B0F17] grid place-items-center font-display font-bold text-sm">
            {user.name?.[0]?.toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p data-testid="sidebar-user-name" className="text-sm text-slate-200 truncate">{user.name}</p>
          <p data-testid="sidebar-user-email" className="text-xs text-slate-500 truncate">{user.email || user.username}</p>
          {user.minds_changed > 0 && (
            <p data-testid="minds-changed-count" className="text-[11px] font-mono text-[#FBBF24] flex items-center gap-1 mt-0.5">
              <Trophy size={11} /> {user.minds_changed} {user.minds_changed === 1 ? "mind" : "minds"} changed
            </p>
          )}
        </div>
        <button data-testid="logout-btn" onClick={onLogout} aria-label="Log out" className="text-slate-500 hover:text-white transition-colors">
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
};
